import React, { useState, useEffect, useRef } from "react";
import "../css/AttendanceChart.css";
import { Doughnut } from "react-chartjs-2";
import axios from "../axiosConfig";
import { BASE_URL } from "../config";
const buildFYOptions = () => {
  const now = new Date();
  const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const years = [];
  for (let y = year; y >= year - 4; y--) {
    years.push({
      label: `FY ${y}-${String(y + 1).slice(-2)}`,
      start: y,
      end: y + 1,
    });
  }
  return years;
};

const FY_OPTIONS = buildFYOptions();
const MONTHS = [
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
  "Jan",
  "Feb",
  "Mar",
];
const QUARTERS = [
  { label: "Q1 (Apr–Jun)", months: [4, 5, 6] },
  { label: "Q2 (Jul–Sep)", months: [7, 8, 9] },
  { label: "Q3 (Oct–Dec)", months: [10, 11, 12] },
  { label: "Q4 (Jan–Mar)", months: [1, 2, 3] },
];
const AttendanceChart = ({ attendanceSummary, expenseSummary, isExpense }) => {
  if (isExpense) return <ExpenseChart expenseSummary={expenseSummary} />;

  if (!attendanceSummary)
    return (
      <div className="flex items-center justify-center p-10 text-gray-400 text-sm font-medium">
        Loading attendance...
      </div>
    );

  const total = attendanceSummary.total ?? 0;
  const present = attendanceSummary.present ?? attendanceSummary.Present ?? 0;
  const absent = attendanceSummary.absent ?? attendanceSummary.Absent ?? 0;
  const onLeave = attendanceSummary.on_leave ?? attendanceSummary.Leave ?? 0;

  const safeTotal = total || present + absent + onLeave || 1;
  const presentPercent = Math.round((present / safeTotal) * 100);
  const absentPercent = Math.round((absent / safeTotal) * 100);
  const leavePercent = Math.round((onLeave / safeTotal) * 100);

  const getDoughnutData = (value, color, trackColor) => ({
    datasets: [
      {
        data: [value, 100 - value],
        backgroundColor: [color, trackColor],
        borderWidth: 0,
      },
    ],
  });

  const cards = [
    {
      label: "Present",
      value: present,
      pct: presentPercent,
      color: "#22c55e",
      trackColor: "#dcfce7",
      textColor: "text-green-600",
      badgeBg: "bg-green-100",
      dotColor: "bg-green-500",
      borderTop: "from-green-500 to-green-400",
    },
    {
      label: "Absent",
      value: absent,
      pct: absentPercent,
      color: "#ef4444",
      trackColor: "#fee2e2",
      textColor: "text-red-600",
      badgeBg: "bg-red-100",
      dotColor: "bg-red-500",
      borderTop: "from-red-500 to-red-400",
    },
    {
      label: "On Leave",
      value: onLeave,
      pct: leavePercent,
      color: "#f97316",
      trackColor: "#ffedd5",
      textColor: "text-orange-600",
      badgeBg: "bg-orange-100",
      dotColor: "bg-orange-500",
      borderTop: "from-orange-500 to-orange-400",
    },
  ];

  return (
    <div className="w-full p-6 font-sans">
      <p className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-5">
        Today's Attendance Overview
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {cards.map((item) => (
          <div
            key={item.label}
            className="relative bg-white rounded-2xl p-6 flex items-center justify-between border border-gray-100 shadow-sm hover:-translate-y-1 hover:shadow-md transition-all duration-200 overflow-hidden"
          >
            {/* Colored top accent bar */}
            <div
              className={`absolute top-0 left-0 right-0 h-1 rounded-t-2xl bg-gradient-to-r ${item.borderTop}`}
            />

            {/* Left: label + count */}
            <div className="flex flex-col gap-2 pt-1">
              {/* Badge */}
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${item.badgeBg} ${item.textColor} w-fit`}
              >
                <span className={`w-2 h-2 rounded-full ${item.dotColor}`} />
                {item.label}
              </span>

              {/* Big number */}
              <span className="text-4xl font-bold text-gray-900 leading-none">
                {item.value}
              </span>

              {/* Subtitle */}
              <span className="text-xs text-gray-400">
                of {total || safeTotal} employees
              </span>
            </div>

            {/* Right: Doughnut ring */}
            <div className="relative w-20 h-20 shrink-0">
              <Doughnut
                data={getDoughnutData(item.pct, item.color, item.trackColor)}
                options={{
                  cutout: "76%",
                  plugins: {
                    legend: { display: false },
                    tooltip: { enabled: false },
                  },
                }}
              />
              <div
                className={`absolute inset-0 flex items-center justify-center text-sm font-bold ${item.textColor}`}
              >
                {item.pct}%
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
const ExpenseChart = ({ expenseSummary }) => {
  const [selectedFY, setSelectedFY] = useState(FY_OPTIONS[0]);
  const [filterMode, setFilterMode] = useState("month");
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const m = new Date().getMonth();
    return m >= 3 ? m - 3 : m + 9;
  });
  const [selectedQuarter, setSelectedQuarter] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fyRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (fyRef.current && !fyRef.current.contains(e.target)) setFyOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const [fyOpen, setFyOpen] = useState(false);

  // Fetch whenever filters change
  useEffect(() => {
    fetchExpenseData();
  }, [selectedFY, filterMode, selectedMonth, selectedQuarter]);

  const fetchExpenseData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        fy_start: selectedFY.start,
        fy_end: selectedFY.end,
        filter: filterMode,
      };

      if (filterMode === "month") {
        params.month =
          selectedMonth < 9 ? selectedMonth + 4 : selectedMonth - 8;
      }
      if (filterMode === "quarter") {
        params.months = QUARTERS[selectedQuarter].months.join(",");
      }

      const res = await axios.get(`${BASE_URL}/api/dashboard/expense-summary`, {
        params,
      });
      const apiData = res.data?.data;
      if (apiData) {
        setData(apiData);
      } else {
        setError("No data returned from server");
        setData(null);
      }
    } catch (err) {
      console.error("Expense summary fetch error:", err);
      setError("Failed to load expense data");
      if (expenseSummary) setData(expenseSummary);
    } finally {
      setLoading(false);
    }
  };
  const src = data || {};
  const total = src.total ?? 0;
  const approved = src.approved ?? 0;
  const unapproved = src.manager_approved ?? src.unapproved ?? 0;
  const pending = src.pending ?? 0;
  const totalAmount = src.total_amount ?? 0;
  const approvedAmt = src.approved_amount ?? 0;
  const unapprovedAmt = src.unapproved_amount ?? 0;
  const pendingAmt = src.pending_amount ?? 0;
  const safeTotal = total || 1;

  const approvedPct = Math.round((approved / safeTotal) * 100);
  const unapprovedPct = Math.round((unapproved / safeTotal) * 100);
  const pendingPct = Math.round((pending / safeTotal) * 100);

  const breakdown = src.breakdown || {};
  const monthlyTrend = src.monthly_trend || [];
  const topSpenders = src.top_spenders || [];

  const fmt = (val) =>
    val >= 100000
      ? `₹${(val / 100000).toFixed(1)}L`
      : val >= 1000
        ? `₹${(val / 1000).toFixed(1)}K`
        : `₹${val ?? 0}`;

  const cards = [
    {
      label: "Approved",
      count: approved,
      pct: approvedPct,
      amount: approvedAmt,
      color: "#16a34a",
      light: "#f0fdf4",
      border: "#bbf7d0",
      track: "#dcfce7",
      sub: "Manager + HR approved",
    },
    {
      label: "Pending HR",
      count: unapproved,
      pct: unapprovedPct,
      amount: unapprovedAmt,
      color: "#dc2626",
      light: "#fff5f5",
      border: "#fecaca",
      track: "#fee2e2",
      sub: "Manager approved, awaiting HR",
    },
    {
      label: "Pending",
      count: pending,
      pct: pendingPct,
      amount: pendingAmt,
      color: "#d97706",
      light: "#fffbeb",
      border: "#fde68a",
      track: "#fef3c7",
      sub: "Not yet approved",
    },
  ];

  const donutData = (pct, color, track) => ({
    datasets: [
      {
        data: [pct, 100 - pct],
        backgroundColor: [color, track],
        borderWidth: 0,
        hoverOffset: 0,
      },
    ],
  });

  const filterLabel = () => {
    if (filterMode === "full") return `${selectedFY.label} (Full Year)`;
    if (filterMode === "quarter")
      return `${selectedFY.label} · ${QUARTERS[selectedQuarter].label}`;
    return `${selectedFY.label} · ${MONTHS[selectedMonth]}`;
  };

  const MONTH_NAMES = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  return (
    <div style={{ padding: "10px 6px 6px" }}>
      {/* ══ HEADER ══ */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
          paddingBottom: 10,
          borderBottom: "1px solid #f1f5f9",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
            Expense Summary
          </span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: loading ? "#94a3b8" : error ? "#dc2626" : "#0369a1",
              background: loading ? "#f8fafc" : error ? "#fff5f5" : "#e0f2fe",
              border: `1px solid ${loading ? "#e2e8f0" : error ? "#fecaca" : "#bae6fd"}`,
              borderRadius: 20,
              padding: "2px 9px",
            }}
          >
            {loading ? "Loading…" : error ? "Error" : filterLabel()}
          </span>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            flexWrap: "wrap",
          }}
        >
          {cards.map((c) => (
            <span
              key={c.label}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontSize: 12,
                color: "#64748b",
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: c.color,
                  display: "inline-block",
                }}
              />
              {c.label}
            </span>
          ))}
          <span
            style={{
              fontSize: 13,
              color: "#475569",
              fontWeight: 600,
              paddingLeft: 10,
              borderLeft: "1px solid #e2e8f0",
            }}
          >
            Total:{" "}
            <span style={{ color: "#0f172a", fontWeight: 800 }}>
              {fmt(totalAmount)}
            </span>
            <span style={{ color: "#94a3b8", fontWeight: 400, marginLeft: 4 }}>
              ({total} claims)
            </span>
          </span>
        </div>
      </div>

      {/* ══ FILTER BAR ══ */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 14,
          padding: "8px 12px",
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: 10,
          flexWrap: "wrap",
        }}
      >
        {/* FY Dropdown */}
        <div ref={fyRef} style={{ position: "relative" }}>
          <button
            onClick={() => setFyOpen((o) => !o)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "5px 11px",
              borderRadius: 7,
              cursor: "pointer",
              border: "1px solid #cbd5e1",
              background: "#fff",
              fontSize: 12,
              fontWeight: 600,
              color: "#334155",
              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
            }}
          >
            📅 {selectedFY.label}
            <span style={{ fontSize: 9, color: "#94a3b8" }}>▼</span>
          </button>
          {fyOpen && (
            <div
              style={{
                position: "absolute",
                top: "calc(100% + 4px)",
                left: 0,
                zIndex: 99,
                background: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: 9,
                boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
                minWidth: 140,
                overflow: "hidden",
              }}
            >
              {FY_OPTIONS.map((fy) => (
                <div
                  key={fy.label}
                  onClick={() => {
                    setSelectedFY(fy);
                    setFyOpen(false);
                  }}
                  style={{
                    padding: "8px 14px",
                    fontSize: 13,
                    cursor: "pointer",
                    fontWeight: selectedFY.label === fy.label ? 700 : 400,
                    color:
                      selectedFY.label === fy.label ? "#0369a1" : "#334155",
                    background:
                      selectedFY.label === fy.label ? "#e0f2fe" : "transparent",
                  }}
                  onMouseEnter={(e) => {
                    if (selectedFY.label !== fy.label)
                      e.currentTarget.style.background = "#f8fafc";
                  }}
                  onMouseLeave={(e) => {
                    if (selectedFY.label !== fy.label)
                      e.currentTarget.style.background = "transparent";
                  }}
                >
                  {fy.label}
                </div>
              ))}
            </div>
          )}
        </div>

        <span style={{ width: 1, height: 22, background: "#e2e8f0" }} />

        {/* Mode tabs */}
        {["month", "quarter", "full"].map((mode) => (
          <button
            key={mode}
            onClick={() => setFilterMode(mode)}
            style={{
              padding: "5px 11px",
              borderRadius: 7,
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 600,
              border:
                filterMode === mode
                  ? "1px solid #bae6fd"
                  : "1px solid transparent",
              background: filterMode === mode ? "#e0f2fe" : "transparent",
              color: filterMode === mode ? "#0369a1" : "#64748b",
              transition: "all 0.15s",
            }}
          >
            {mode === "month"
              ? "Monthly"
              : mode === "quarter"
                ? "Quarterly"
                : "Full Year"}
          </button>
        ))}

        {filterMode !== "full" && (
          <span style={{ width: 1, height: 22, background: "#e2e8f0" }} />
        )}

        {/* Month selector */}
        {filterMode === "month" && (
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {MONTHS.map((m, i) => (
              <button
                key={m}
                onClick={() => setSelectedMonth(i)}
                style={{
                  padding: "4px 9px",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontSize: 11,
                  fontWeight: 600,
                  border:
                    selectedMonth === i
                      ? "1px solid #16a34a"
                      : "1px solid #e2e8f0",
                  background: selectedMonth === i ? "#f0fdf4" : "#fff",
                  color: selectedMonth === i ? "#16a34a" : "#64748b",
                  transition: "all 0.12s",
                }}
              >
                {m}
              </button>
            ))}
          </div>
        )}

        {/* Quarter selector */}
        {filterMode === "quarter" && (
          <div style={{ display: "flex", gap: 6 }}>
            {QUARTERS.map((q, i) => (
              <button
                key={q.label}
                onClick={() => setSelectedQuarter(i)}
                style={{
                  padding: "5px 11px",
                  borderRadius: 7,
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 600,
                  border:
                    selectedQuarter === i
                      ? "1px solid #7c3aed"
                      : "1px solid #e2e8f0",
                  background: selectedQuarter === i ? "#f5f3ff" : "#fff",
                  color: selectedQuarter === i ? "#7c3aed" : "#64748b",
                  transition: "all 0.12s",
                }}
              >
                {q.label.split(" ")[0]}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ══ CARDS ══ */}
      {loading ? (
        <div
          style={{
            textAlign: "center",
            padding: "30px 0",
            color: "#94a3b8",
            fontSize: 14,
          }}
        >
          Loading expense data…
        </div>
      ) : error && !data ? (
        <div
          style={{
            textAlign: "center",
            padding: "30px 0",
            color: "#dc2626",
            fontSize: 14,
          }}
        >
          {error}
        </div>
      ) : (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 14,
              opacity: loading ? 0.5 : 1,
              transition: "opacity 0.2s",
            }}
          >
            {cards.map((c) => (
              <div
                key={c.label}
                style={{
                  background: c.light,
                  border: `1.5px solid ${c.border}`,
                  borderRadius: 14,
                  padding: "18px 20px 14px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 14,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                  transition: "box-shadow 0.18s, transform 0.18s",
                  cursor: "default",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow =
                    "0 6px 20px rgba(0,0,0,0.09)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow =
                    "0 1px 3px rgba(0,0,0,0.04)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                {/* Label + Amount + Donut */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: c.color,
                        textTransform: "uppercase",
                        letterSpacing: "0.6px",
                        marginBottom: 2,
                      }}
                    >
                      {c.label}
                    </div>
                    <div
                      style={{
                        fontSize: 10,
                        color: "#94a3b8",
                        marginBottom: 6,
                      }}
                    >
                      {c.sub}
                    </div>
                    <div
                      style={{
                        fontSize: 26,
                        fontWeight: 800,
                        color: "#0f172a",
                        letterSpacing: "-1px",
                        lineHeight: 1,
                      }}
                    >
                      {fmt(c.amount)}
                    </div>
                  </div>
                  <div
                    style={{
                      position: "relative",
                      width: 72,
                      height: 72,
                      flexShrink: 0,
                    }}
                  >
                    <Doughnut
                      data={donutData(c.pct, c.color, c.track)}
                      options={{
                        cutout: "68%",
                        plugins: {
                          legend: { display: false },
                          tooltip: { enabled: false },
                        },
                        animation: { duration: 800 },
                      }}
                    />
                    <span
                      style={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 14,
                        fontWeight: 800,
                        color: c.color,
                      }}
                    >
                      {c.pct}%
                    </span>
                  </div>
                </div>

                {/* Claims count */}
                <div style={{ fontSize: 13, color: "#64748b" }}>
                  <span
                    style={{
                      fontSize: 18,
                      fontWeight: 800,
                      color: "#334155",
                      marginRight: 3,
                    }}
                  >
                    {c.count}
                  </span>
                  of {total} claims
                </div>

                {/* Progress bar */}
                <div
                  style={{
                    height: 6,
                    borderRadius: 99,
                    background: c.track,
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${c.pct}%`,
                      height: "100%",
                      borderRadius: 99,
                      background: c.color,
                      transition: "width 0.9s cubic-bezier(0.4,0,0.2,1)",
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <style>{`
        @media (max-width: 680px) {
          .exp-resp-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default AttendanceChart;
